import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ShowCard from '../widgets/ShowCard';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  
  &:hover {
    background: ${props => props.theme.colors.surface};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.h2.fontSize};
  font-weight: ${props => props.theme.typography.h2.fontWeight};
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ShowsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.lg};
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FilterButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['active'].includes(prop),
})`
  background: ${props => props.active ? props.theme.colors.accent : props.theme.colors.surface};
  color: ${props => props.active ? 'white' : props.theme.colors.text.primary};
  border: 1px solid ${props => props.active ? props.theme.colors.accent : props.theme.colors.border};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.accent : props.theme.colors.surfaceVariant};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.text.secondary};
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
`;

const MyShows = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'created', 'liked'

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    const fetchShows = async () => {
      try {
        const data = await apiService.getUserShows();
        setShows(data || []);
      } catch (error) {
        console.error('Error fetching shows:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShows();
  }, [isAuthenticated, navigate]);

  const filteredShows = shows.filter(show => {
    if (filter === 'all') return true;
    if (filter === 'created') return show.show_type === 'created';
    if (filter === 'liked') return show.show_type === 'liked';
    return true;
  });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowLeftIcon />
        </BackButton>
        <Title>My Shows</Title>
      </Header>

      <Content>
        <FilterContainer>
          <FilterButton 
            active={filter === 'all'} 
            onClick={() => setFilter('all')}
          >
            All Shows
          </FilterButton>
          <FilterButton 
            active={filter === 'created'} 
            onClick={() => setFilter('created')}
          >
            Created Shows
          </FilterButton>
          <FilterButton 
            active={filter === 'liked'} 
            onClick={() => setFilter('liked')}
          >
            Saved Shows
          </FilterButton>
        </FilterContainer>

        {loading ? (
          <EmptyState>Loading your shows...</EmptyState>
        ) : filteredShows.length === 0 ? (
          <EmptyState>
            {filter === 'all' 
              ? "You haven't created or saved any shows yet."
              : filter === 'created'
              ? "You haven't created any shows yet."
              : "You haven't saved any shows yet."}
          </EmptyState>
        ) : (
          <ShowsGrid>
            {filteredShows.map(show => (
              <ShowCard
                key={show.id}
                show={show}
                showEditButton={true}
              />
            ))}
          </ShowsGrid>
        )}
      </Content>
    </Container>
  );
};

export default MyShows; 